import {
  Box,
  Icon,
  SimpleGrid,
  Stack,
  Text,
} from '@chakra-ui/react';
import { AddIcon, CalendarIcon, SearchIcon, EmailIcon } from '@chakra-ui/icons';
import { FiArrowUpRight } from 'react-icons/fi';
import { ComponentType, ElementType } from 'react';
import { useNavigate } from 'react-router-dom';

type QuickActionsBarProps = {
  orgId: string;
};

type QuickAction = {
  label: string;
  icon: ComponentType<any>;
  onClick: () => void;
};

const QuickActionsBar = ({ orgId }: QuickActionsBarProps) => {
  const navigate = useNavigate();

  const actions: QuickAction[] = [
    {
      label: 'Add lead',
      icon: AddIcon,
      onClick: () => navigate(`/organization/${orgId}/leads/new`),
    },
    {
      label: 'Review tasks',
      icon: CalendarIcon,
      onClick: () => navigate(`/organization/${orgId}/tasks`),
    },
    {
      label: 'Open pipeline',
      icon: SearchIcon,
      onClick: () => navigate(`/organization/${orgId}/leads-dashboard`),
    },
    {
      label: 'Team chat',
      icon: EmailIcon,
      onClick: () => navigate(`/organization/${orgId}/teamchat/conversations/`),
    },
  ];

  return (
    <Box
      bg="white"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius={{ base: '20px', md: '24px' }}
      px={{ base: 4, md: 6 }}
      py={{ base: 4, md: 5 }}
      boxShadow="0 18px 40px rgba(15, 23, 42, 0.05)"
    >
      <Stack
        direction={{ base: 'column', md: 'row' }}
        justify="space-between"
        align={{ base: 'flex-start', md: 'center' }}
        mb={5}
        spacing={3}
      >
        <Box>
          <Text fontSize="xs" fontWeight="800" letterSpacing="0.12em" color="gray.500" textTransform="uppercase">
            Quick Actions
          </Text>
          <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight="800" color="gray.900">
            Move work forward
          </Text>
        </Box>
      </Stack>

      <SimpleGrid columns={{ base: 1, sm: 2, xl: 4 }} spacing={3}>
        {actions.map((action) => (
          <Box
            key={action.label}
            role="button"
            onClick={action.onClick}
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="18px"
            px={4}
            py={5}
            bg="white"
            cursor="pointer"
            transition="all 0.18s ease"
            _hover={{
              bg: '#F8FAFC',
              borderColor: 'green.300',
              boxShadow: '0 12px 28px rgba(15, 23, 42, 0.08)',
              transform: 'translateY(-1px)',
            }}
          >
            <Stack spacing={5}>
              <Stack direction="row" justify="space-between" align="center">
                <Box
                  boxSize="56px"
                  borderRadius="18px"
                  bg="#E7F5EE"
                  color="#166534"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  boxShadow="inset 0 1px 0 rgba(255, 255, 255, 0.7)"
                >
                  <Icon as={action.icon as ElementType} boxSize={6} />
                </Box>
                <Icon as={FiArrowUpRight as ElementType} color="gray.400" boxSize={5} />
              </Stack>
              <Text fontWeight="700" fontSize="lg" color="gray.900">
                {action.label}
              </Text>
            </Stack>
          </Box>
        ))}
      </SimpleGrid>
    </Box>
  );
};

export default QuickActionsBar;
